import { isInWebView } from '../../../utils/isInWebView';
import { MobileFooter } from 'components/molecules/MobileFooter/MobileFooter';
import { RegistrationFrameConfig } from 'InternalLibrary';
import { RegistrationGlobalComponentProps } from 'types';
import { Stepper } from 'components/molecules';
import { useCallback, useEffect } from 'react';
import classNames from 'classnames';
import LogoutButton from 'components/molecules/LogoutButton/LogoutButton';
import React from 'react';
import styles from './Frame.module.scss';
import useEventCollector from '../../../hooks/eventCollector/useEventCollector';
import VersionInfo from 'components/atoms/VersionInfo/VersionInfo';

export interface RegistrationFrameProps extends RegistrationFrameConfig {
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  bodyParentWrapperClassName?: string;
  bodyWrapperClassName?: string;
  onCalculatorOpen?: () => void;
  onCalculatorClose?: () => void;
  onBackBtnHandler?: () => void;
  notifications?: Array<React.ReactNode>;
  inWebView?: boolean;
}

export const Frame: React.FC<RegistrationFrameProps> = (props) => {
  const { reset } = useEventCollector();
  const {
    pageTitle,
    bodyClassName,
    className,
    children,
    panel: Panel,
    navbar: Navbar,
    calculatorDesktop: CalculatorDesktop,
    calculatorMobile: CalculatorMobile,
    onLogoutButtonClick,
    onCalculatorOpen,
    onCalculatorClose,
    footerDesktop,
    footerMobile,
    notifications,
    hideLogout,
    inWebView = isInWebView(),
    ...inheritedGlobalProps
  } = props;

  useEffect(() => {
    if (pageTitle !== undefined) {
      document.title = pageTitle;
    }
  }, [pageTitle]);

  const { sections, sectionID, hideMobileFooter, frameContent } =
    inheritedGlobalProps;

  const [calculatorOpened, setCalculatorOpened] = React.useState(false);
  const handleCalculatorOpen = React.useCallback(() => {
    setCalculatorOpened(true);
    onCalculatorOpen?.();
  }, [onCalculatorOpen]);
  const handleCalculatorClose = useCallback(() => {
    setCalculatorOpened(false);
    onCalculatorClose?.();
  }, [onCalculatorClose]);
  const handleCalculatorToggle = useCallback(() => {
    if (calculatorOpened) {
      handleCalculatorClose();
    } else {
      handleCalculatorOpen();
    }
  }, [calculatorOpened, handleCalculatorClose, handleCalculatorOpen]);
  const handleLogout = useCallback(() => {
    onLogoutButtonClick?.();
    reset();
  }, [onLogoutButtonClick, reset]);

  const globalProps: RegistrationGlobalComponentProps = {
    ...props,
    onCalculatorToggle: handleCalculatorToggle,
    onCalculatorClose: handleCalculatorClose,
    onCalculatorOpen: handleCalculatorOpen,
    calculatorOpened,
  };

  return (
    <div className={classNames(styles['wrapper'], className)} data-test="frame">
      {/* Navbar / Header */}
      {Navbar && !globalProps.hidePanel && (
        <div className={styles['navbar']}>
          <Navbar {...globalProps} showTop={!inWebView} />
        </div>
      )}

      {/* Panel / Sidebar */}
      {Panel && !globalProps.hidePanel && (
        <div className={styles['left-panel']} data-test="frame-left-panel">
          <Panel
            {...globalProps}
            hideEditButton={
              CalculatorDesktop === undefined || MobileFooter === undefined
                ? true
                : globalProps.hideEditButton
            }
          />
        </div>
      )}

      {/* Sections */}
      {sections && sectionID !== undefined && (
        <div
          className={styles['left-panel-secondary']}
          data-test="frame-left-panel-secondary"
        >
          <div className={styles['logo']} data-test="css-Logo">
            {props.logo}
          </div>
          <Stepper
            steps={sections.map(({ title }) => ({ text: title }))}
            currentStep={sections.findIndex(({ id }) => id === sectionID)}
            dataTest="css-Progressbar-Items"
          />
          {CalculatorDesktop && (
            <div
              className={classNames(
                styles['left-panel-secondary-overlay'],
                calculatorOpened
                  ? styles['left-panel-secondary-overlay--opened']
                  : undefined,
              )}
            >
              <CalculatorDesktop
                onCalculatorClose={globalProps.onCalculatorClose}
              />
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div
        className={classNames(
          styles['body'],
          footerDesktop && styles['wrapper-direction-column'],
          props.bodyParentWrapperClassName,
          calculatorOpened && styles['body-overlay--opened'],
          bodyClassName,
        )}
        data-test="frame-body"
      >
        <div
          className={classNames(
            styles['body-wrapper'],
            props.bodyWrapperClassName,
          )}
          data-test="frame-body-wrapper"
        >
          {children}
        </div>
        {!hideLogout && (
          <div
            className={styles['logout-button-wrapper']}
            data-test="logout-button-wrapper"
          >
            {!hideLogout && (
              <LogoutButton onClick={handleLogout}>
                {frameContent.logout}
              </LogoutButton>
            )}
          </div>
        )}

        {/* Footer */}
        {!hideMobileFooter && !inWebView && (
          <MobileFooter
            content={frameContent}
            onLogoutButtonClick={onLogoutButtonClick}
            hideLogout={hideLogout}
          />
        )}

        {/* Overlays / Others */}
        <VersionInfo />
        {CalculatorMobile && (
          <div
            className={classNames(
              styles['body-overlay'],
              calculatorOpened ? styles['body-overlay--opened'] : undefined,
            )}
          >
            <div className={classNames(styles['body-overlay-content'])}>
              <CalculatorMobile {...globalProps} />
            </div>
            <div
              onClick={handleCalculatorClose}
              className={classNames(styles['body-overlay-shadow'])}
            />
          </div>
        )}
        {notifications && (
          <div className={styles['notifications']}>
            {notifications.map((note, index) => (
              <div key={index} className={styles.note}>
                {note}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
